import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Player from "../player/Player";
import GameEndScreen from "../GameEndScreen";
import SplashScreen from "../SplashScreen";
import { iconMap } from "../gameIconsUtils";
import { CgSandClock } from "react-icons/cg";
import { playerSpriteSets } from "../player/playerIconsUtils";

const PLAYER_WIDTH = 100;
const PLAYER_HEIGTH = 100;
const PLAYER_SPEED = 12;
const OBJECT_WIDTH = 70;
const OBJECT_HEIGTH = 70;
const FALL_SPEED = 5;
const FALL_INTERVAL = 2000;
const MAX_PLAY_ZONE = 600;
const GAME_DURATION = 45;
const MIN_CORRECT_ITEMS = 6;

const FallingObjectsGame = ({ question, handleNext, setQuizResults }) => {
	// Game area
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [windowHeight, setWindowHeight] = useState(window.innerHeight);
	const gameWidth = Math.min(windowWidth, MAX_PLAY_ZONE);
	const gameHeight = windowHeight;
	const fallInterval =
		FALL_INTERVAL - Math.floor(parseInt(question?.difficulty) * 50);
	const [correctItemSchedule, setCorrectItemSchedule] = useState([]);
	const [startTime, setStartTime] = useState(null);

	// Track which items have been scored already
	const scoredItemsRef = useRef(new Set());
	// Game state
	const [gameStarted, setGameStarted] = useState(false);
	const [splashScreenText, setSplashScreenText] = useState(
		"Recupérez les items necessaire a la bonne execution de l'intervention suivante. Attention, tous les equipements n'ont pas besoin d'être collectés..."
	);
	const [splashScreenTitle, setSplashScreenTitle] = useState(
		"Equipez vous avant l'intervention suivante..."
	);
	const [situation, setSituation] = useState(null);
	const [background, setBackground] = useState(null);
	const [gameEnded, setGameEnded] = useState(false);
	const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
	const [collectedItems, setCollectedItems] = useState([]);

	// Objects and items
	const [fallingObjects, setFallingObjects] = useState([]);
	const [items, setItems] = useState([]);
	const [objectPoints, setObjectPoints] = useState(null);

	// Player state
	const playerRef = useRef({ x: gameWidth / 2, y: gameHeight - 50 });
	const [player, setPlayer] = useState(playerRef.current);
	const directionRef = useRef({ dx: 0, dy: 0 });
	const [direction, setDirection] = useState(directionRef.current);

	// Track pressed keys
	const keysPressed = useRef({});

	const [score, setScore] = useState(0);
	const scoreRef = useRef(0);

	const playerProfile = useMemo(() => {
		return localStorage.getItem("player")
			? JSON.parse(localStorage.getItem("player"))
			: {};
	}, []);

	const playerSpriteSet =
		playerProfile?.avatar?.gender === "male"
			? playerSpriteSets.man_turquoise
			: playerSpriteSets.woman_purple;

	const isTouchDevice = () => {
		return "ontouchstart" in window || navigator.maxTouchPoints > 0;
	};

	// Resize event listener
	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
			setWindowHeight(window.innerHeight);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		initVariables();
	}, [question]);

	const initVariables = () => {
		setGameEnded(false);
		setGameStarted(false);
		setSplashScreenTitle("Equipez vous avant l'intervention suivante...");
		setSplashScreenText(question.text);
		if (question?.options?.situation) {
			setSituation(iconMap[question.options.situation] || null);
			setBackground(iconMap[question.options.background] || null);
		}

		keysPressed.current = {};
		setTimeLeft(GAME_DURATION);
		setFallingObjects([]);
		setCollectedItems([]);
		scoreRef.current = 0;
		setScore(0);
		playerRef.current = { x: gameWidth / 2, y: gameHeight - 50 };
		setPlayer({ x: gameWidth / 2, y: gameHeight - 50 });
		directionRef.current = { dx: 0, dy: 0 };
		setDirection({ dx: 0, dy: 0 });

		if (question?.options) {
			setItems(
				question.options.items.map((item) => ({
					...item,
					icon: iconMap[item.icon] || null,
				}))
			);
		}
	};

	const startGame = () => {
		initVariables();
		setGameStarted(true);
		setGameEnded(false);
	};

	const endGame = () => {
		setGameStarted(false);
		setGameEnded(true);
	};

	// Countdown timer
	useEffect(() => {
		if (!gameStarted || gameEnded) return;
		const timer = setInterval(() => {
			setTimeLeft((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					endGame();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(timer);
	}, [gameStarted, gameEnded]);

	// Handle movement (continuous update)
	useEffect(() => {
		if (!gameStarted || gameEnded || isTouchDevice()) return;

		const handleKeyDown = (e) => {
			if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
				keysPressed.current[e.key] = true;
			}
		};

		const handleKeyUp = (e) => {
			if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
				keysPressed.current[e.key] = false;
			}
		};

		const moveInterval = setInterval(() => {
			const dx =
				(keysPressed.current["ArrowRight"] ? 1 : 0) -
				(keysPressed.current["ArrowLeft"] ? 1 : 0);
			const dy = 0;

			if (dx !== direction.dx || dy !== direction.dy) {
				setDirection({ dx, dy });
			}

			if (dx !== 0 || dy !== 0) {
				playerRef.current = {
					x: Math.max(
						PLAYER_WIDTH / 4,
						Math.min(
							playerRef.current.x + dx * PLAYER_SPEED,
							gameWidth - PLAYER_WIDTH / 4
						)
					),
					y: gameHeight - 50,
				};
				setPlayer({ ...playerRef.current });
			} else {
				setDirection({ dx: 0, dy: 0 });
			}
		}, 50);

		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("keyup", handleKeyUp);

		return () => {
			clearInterval(moveInterval);
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("keyup", handleKeyUp);
		};
	}, [gameStarted, gameEnded, gameWidth, gameHeight]);

	// Player movement on mobile
	useEffect(() => {
		if (!gameStarted || gameEnded || !isTouchDevice()) return;

		let touchStart = null;
		let lastTouch = null;
		let interval = null;

		const handleTouchStart = (e) => {
			const touch = e.touches[0];
			touchStart = { x: touch.clientX, y: touch.clientY };
			lastTouch = { ...touchStart };

			interval = setInterval(() => {
				if (!touchStart || !lastTouch) return;

				const dx = lastTouch.x - touchStart.x;
				const dy = lastTouch.y - touchStart.y;
				const threshold = 20;

				let moveDirection = { dx: 0, dy: 0 };
				if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > threshold) {
					moveDirection.dx = dx > 0 ? 1 : -1;
				}

				// ✅ Always update direction (to keep animation alive)
				directionRef.current = moveDirection;
				setDirection((prev) => {
					if (prev.dx !== moveDirection.dx || prev.dy !== moveDirection.dy) {
						return moveDirection;
					}
					return prev;
				});

				// ✅ Move player
				playerRef.current = {
					x: Math.max(
						PLAYER_WIDTH / 4,
						Math.min(
							playerRef.current.x + moveDirection.dx * PLAYER_SPEED,
							gameWidth - PLAYER_WIDTH / 4
						)
					),
					y: gameHeight - 50,
				};
				setPlayer({ ...playerRef.current });
			}, 50);
		};

		const handleTouchMove = (e) => {
			const touch = e.touches[0];
			lastTouch = { x: touch.clientX, y: touch.clientY };
		};

		const handleTouchEnd = () => {
			touchStart = null;
			lastTouch = null;
			clearInterval(interval);
			setDirection({ dx: 0, dy: 0 }); // stop moving & animation
		};

		document.addEventListener("touchstart", handleTouchStart, {
			passive: false,
		});
		document.addEventListener("touchmove", handleTouchMove, { passive: false });
		document.addEventListener("touchend", handleTouchEnd, { passive: false });

		return () => {
			document.removeEventListener("touchstart", handleTouchStart);
			document.removeEventListener("touchmove", handleTouchMove);
			document.removeEventListener("touchend", handleTouchEnd);
			clearInterval(interval);
		};
	}, [gameStarted, gameEnded]);

	useEffect(() => {
		if (!gameStarted) return;

		// Save the time when the game starts
		const now = Date.now();
		setStartTime(now);

		// Calculate total spawn count
		const totalSpawns = Math.floor((GAME_DURATION * 1000) / fallInterval);

		// Pick MIN_CORRECT_ITEMS unique spawn indices
		const indices = new Set();
		while (indices.size < MIN_CORRECT_ITEMS) {
			indices.add(Math.floor(Math.random() * totalSpawns));
		}
		setCorrectItemSchedule([...indices]);
	}, [gameStarted]);

	// spawn objects
	useEffect(() => {
		if (!gameStarted || startTime === null) return;

		let spawnCount = 0;
		const interval = setInterval(() => {
			const isCorrectScheduled = correctItemSchedule.includes(spawnCount);
			const obj = getRandomObject(isCorrectScheduled);
			setFallingObjects((prev) => [...prev, obj]);
			spawnCount++;
		}, fallInterval);

		return () => clearInterval(interval);
	}, [gameStarted, correctItemSchedule, startTime]);

	// move objects and chek for collisions
	useEffect(() => {
		if (!gameStarted || gameEnded) return;

		const fallInterval = setInterval(() => {
			setFallingObjects(
				(prevFallingObjects) =>
					prevFallingObjects
						.map((obj) => {
							// Move object down
							const newY = obj.y + FALL_SPEED;

							// Check collision using playerRef (No re-renders!)
							const collision =
								newY >= playerRef.current.y - PLAYER_HEIGTH &&
								newY <= playerRef.current.y &&
								obj.x + OBJECT_WIDTH / 2 <
									playerRef.current.x + PLAYER_WIDTH / 2 &&
								obj.x + OBJECT_WIDTH / 2 >
									playerRef.current.x - PLAYER_WIDTH / 2;

							if (collision) {
								// Add to collected items
								setCollectedItems((prev) => [...prev, obj]);
								setObjectPoints({
									points: obj.isCorrect ? 1 : -1,
									x: obj.x,
									y: obj.y,
								});
								setTimeout(() => {
									setObjectPoints(null);
								}, 1000);
								return null; // ✅ Remove from falling objects
							}

							// Keep only objects within the screen
							return newY < gameHeight ? { ...obj, y: newY } : null;
						})
						.filter(Boolean) // Remove null values (collected or out-of-screen)
			);
		}, 50);

		return () => clearInterval(fallInterval);
	}, [gameStarted, gameEnded, gameHeight]);

	// UPDATE SCORE

	useEffect(() => {
		if (collectedItems.length === 0) return;

		let _score = score;

		collectedItems.forEach((item) => {
			const itemKey = `${item.x}-${item.y}`; // Or item.id if unique

			if (!scoredItemsRef.current.has(itemKey)) {
				scoredItemsRef.current.add(itemKey);
				_score += item.isCorrect ? 1 : -1;

				setObjectPoints({
					points: item.isCorrect ? 1 : -1,
					x: item.x,
					y: item.y,
				});
				setTimeout(() => {
					setObjectPoints(null);
				}, 1000);
			}
		});

		if (_score !== score) {
			setScore(_score);
			if (scoreRef.current) {
				scoreRef.current.classList.remove("score-up");
				void scoreRef.current.offsetWidth;
				scoreRef.current.classList.add("score-up");
			}
		}
	}, [collectedItems]);

	// Generate a random falling object
	const getRandomObject = (forceCorrect = false) => {
		const filteredItems = forceCorrect
			? items.filter((i) => i.isCorrect)
			: items;

		const obj = filteredItems[Math.floor(Math.random() * filteredItems.length)];
		return {
			...obj,
			x: Math.max(OBJECT_WIDTH, Math.random() * (gameWidth - OBJECT_WIDTH)),
			y: 0,
		};
	};

	const handleNextLevel = () => {
		let score = collectedItems.reduce(
			(total, item) => total + (item.isCorrect ? 1 : -1),
			0
		);
		let answerValue = {
			collectedItems: collectedItems.filter(
				(item, index, self) =>
					index === self.findIndex((t) => t.x === item.x && t.y === item.y)
			),
		};
		setQuizResults((prev) => ({
			...prev,
			score: prev.score + score,
			responses: {
				...prev.responses,
				[question.id]: answerValue,
			},
		}));
		handleNext();
	};

	return (
		<div
			className="relative w-full flex justify-center items-center"
			style={{
				height: "100%", // Now works, thanks to SafeScreen
			}}
		>
			<div
				className="relative h-full w-full flex justify-center items-center"
				style={{
					width: `${gameWidth}px`,
					// height: `${gameHeight}px`,
				}}
			>
				{!gameStarted && !gameEnded && (
					<SplashScreen
						title={splashScreenTitle}
						text={splashScreenText}
						situation={situation}
						onStart={startGame}
						items={items}
						itemsLabel={
							[
								"303b774b-c256-4829-bb24-32c739ec8301",
								"fd159289-245b-432d-aec1-b2a3199bee86",
								"72823620-0094-48ca-b359-0777b6a96bf6",
								"d46d6b0d-1a83-4517-be91-cceb71f6e4b1",
							].includes(question.id)
								? "Positions à choisir :"
								: "Équipements à disposition :"
						}
					/>
				)}
				{gameEnded && (
					<GameEndScreen
						title="Temps écoulé !"
						collectedItems={collectedItems.filter(
							(item, index, self) =>
								index ===
								self.findIndex((t) => t.x === item.x && t.y === item.y)
						)}
						onNextLevel={handleNextLevel}
						levelCompleted={true}
						onReplay={startGame}
						correctItemsLabel={
							[
								"303b774b-c256-4829-bb24-32c739ec8301",
								"fd159289-245b-432d-aec1-b2a3199bee86",
								"72823620-0094-48ca-b359-0777b6a96bf6",
								"d46d6b0d-1a83-4517-be91-cceb71f6e4b1",
							].includes(question.id)
								? "Positions correctes choisies :"
								: "Équipements utiles ramassés :"
						}
						wrongItemsLabel={
							[
								"303b774b-c256-4829-bb24-32c739ec8301",
								"fd159289-245b-432d-aec1-b2a3199bee86",
								"72823620-0094-48ca-b359-0777b6a96bf6",
								"d46d6b0d-1a83-4517-be91-cceb71f6e4b1",
							].includes(question.id)
								? "Positions incorrectes choisies :"
								: "Équipements inutiles ramassés :"
						}
					/>
				)}

				{background && (
					<img
						alt={""}
						src={background}
						className="absolute bottom-0 left-0 h-full w-full object-cover"
					/>
				)}
				<div className="absolute z-50 top-4 left-4 w-40 flex gap-2 items-center bg-white p-2 px-4 rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] font-orbitron text-lg font-bold text-gray-600">
					Score :
					<span ref={scoreRef} className="score-up text-[#fbb554]">
						{score}
					</span>
				</div>
				<div className="absolute z-50 top-4 right-4 flex gap-2 items-center w-32 bg-white p-2 px-4 rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] font-orbitron text-lg font-bold text-[#fbb554]">
					<CgSandClock size={28} />
					<span className="text-gray-600">
						{Math.floor(timeLeft / 60)}:
						{(timeLeft % 60).toString().padStart(2, "0")}
					</span>
				</div>

				<AnimatePresence>
					{fallingObjects.map((obj, index) => (
						<motion.div
							key={index}
							className="absolute z-12 bg-white border-blue-600  border-8 p-5"
							style={{
								width: OBJECT_WIDTH + 40,
								height: OBJECT_HEIGTH + 40,
								left: obj.x - (OBJECT_WIDTH + 20) / 2,
								top: obj.y - (OBJECT_HEIGTH + 20) / 2,
							}}
						>
							<motion.img
								key={index}
								src={obj.icon}
								className=""
								style={{
									width: OBJECT_WIDTH,
									height: OBJECT_HEIGTH,
									left: obj.x - OBJECT_WIDTH / 2,
									top: obj.y - OBJECT_HEIGTH / 2,
								}}
							/>
						</motion.div>
					))}
				</AnimatePresence>

				{objectPoints && (
					<div
						className="absolute z-50 text-[#fbb554] text-xl score-up bg-white aspect-square p-2 rounded-full font-orbitron font-bold"
						style={{
							left: objectPoints.x,
							top: objectPoints.y - 30,
						}}
					>
						{Math.sign(objectPoints.points)}
					</div>
				)}

				<>
					<Player
						position={player}
						direction={direction}
						height={PLAYER_HEIGTH + "px"}
						width={PLAYER_WIDTH + "px"}
						spriteSet={playerSpriteSet}
					/>
				</>
			</div>
		</div>
	);
};

export default FallingObjectsGame;
